import { ensureOneSignalInitialized, getOneSignal } from './oneSignalPageSdk';

const STORAGE_KEY = 'onesignal_player_id_sent';

function getExternalUserId(user) {
  if (!user || typeof user !== 'object') return '';
  return String(user.uuid ?? user.id ?? '').trim();
}

/**
 * Sends only the id OneSignal provides: `OneSignal.User.PushSubscription.id` (web push subscription / player id).
 * Logged-in user is identified by the Bearer token, not this field.
 */
export async function savePlayerIdToBackend(playerId) {
  if (playerId == null || playerId === '') return;
  const normalized = String(playerId).trim();
  if (!normalized) return;
  const prev = localStorage.getItem(STORAGE_KEY);
  if (prev === normalized) return;

  const formData = new FormData();
  formData.append('onesignal_player_id', normalized);

  // Dynamic import avoids circular dependency with authSlice → oneSignalPush → store
  const [{ store }, { authApi }] = await Promise.all([
    import('../store'),
    import('../store/api/authApi'),
  ]);
  await store.dispatch(authApi.endpoints.savePlayerId.initiate(formData)).unwrap();
  localStorage.setItem(STORAGE_KEY, normalized);
}

function readSubscriptionIdRaw() {
  const OneSignal = getOneSignal();
  const id = OneSignal?.User?.PushSubscription?.id;
  if (id == null || id === '') return null;
  return String(id).trim();
}

async function trySendCurrentSubscription() {
  const id = readSubscriptionIdRaw();
  if (id) await savePlayerIdToBackend(id);
}

const POLL_MS = 500;
const POLL_ATTEMPTS = 80;

/**
 * After login: OneSignal.init via official deferred pattern, link user, then POST subscription id.
 */
export async function registerOneSignalForLoggedInUser(user) {
  const appId = process.env.REACT_APP_ONESIGNAL_APP_ID?.trim();
  if (!appId) {
    if (process.env.NODE_ENV === 'development') {
      console.info('[OneSignal] Set REACT_APP_ONESIGNAL_APP_ID in .env and restart the dev server.');
    }
    return;
  }

  let OneSignal;
  try {
    OneSignal = await ensureOneSignalInitialized();
  } catch (e) {
    const msg = e?.message ?? String(e);
    if (typeof msg === 'string' && msg.includes('Can only be used on')) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      console.warn(
        '[OneSignal] Site URL mismatch. This page is',
        origin || '(unknown)',
        'but your OneSignal app is restricted to another origin (see message below).',
        'Fix: OneSignal Dashboard → Settings → Web push → set Site URL to',
        origin || 'your production https URL',
        '(no path). Then save.\n',
        msg
      );
    } else {
      console.warn('[OneSignal] init failed', msg);
    }
    return;
  }

  if (!OneSignal) return;

  // Ask for browser permission first so a web push subscription + token exist before we sync state.
  // Calling PushSubscription.optIn() too early PATCHes with notification_types while the subscription is still
  // disabled → OneSignal API error subscription-10 ("can't change notification_types ... while disabled").
  try {
    if (typeof OneSignal.Notifications?.requestPermission === 'function') {
      await OneSignal.Notifications.requestPermission();
    } else if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  } catch (e) {
    console.warn('[OneSignal] requestPermission:', e?.message || e);
  }

  const externalId = getExternalUserId(user);
  if (externalId) {
    try {
      await OneSignal.login(externalId);
    } catch (e) {
      console.warn('OneSignal.login failed', e);
    }
  }

  OneSignal.User.PushSubscription.removeEventListener('change', onPushSubscriptionChange);
  OneSignal.User.PushSubscription.addEventListener('change', onPushSubscriptionChange);

  await trySendCurrentSubscription();

  for (let i = 0; i < POLL_ATTEMPTS; i++) {
    const id = readSubscriptionIdRaw();
    if (id) {
      await savePlayerIdToBackend(id);
      return;
    }
    await new Promise((r) => setTimeout(r, POLL_MS));
  }

  if (process.env.NODE_ENV === 'development') {
    console.warn(
      "[OneSignal] No subscription id yet — allow notifications, use HTTPS (or localhost), and ensure public/OneSignalSDKWorker.js is served. save-player-id only sends OneSignal's subscription id."
    );
  }
}

function onPushSubscriptionChange() {
  trySendCurrentSubscription().catch((e) => console.warn('OneSignal save player id failed', e));
}

export async function logoutOneSignal() {
  localStorage.removeItem(STORAGE_KEY);
  if (!process.env.REACT_APP_ONESIGNAL_APP_ID?.trim()) return;

  const OneSignal = getOneSignal();
  if (!OneSignal) return;

  try {
    OneSignal.User.PushSubscription.removeEventListener('change', onPushSubscriptionChange);
    await OneSignal.logout();
  } catch (e) {
    console.warn('OneSignal.logout failed', e);
  }
}
